import React, { useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, setDoc, deleteDoc, arrayUnion, arrayRemove, increment, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { HeartIcon as HeartSolidIcon, BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import { HeartIcon, ChatBubbleLeftIcon, ShareIcon, BookmarkIcon, ArrowPathRoundedSquareIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';
import AuthContext from '../../contexts/AuthContext';
import CommentSection from './CommentSection';
import FormattedText from '../common/FormattedText';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { generateId, announceToScreenReader, handleKeyboardActivation } from '../../utils/accessibility';
import ImageLightbox from '../common/ImageLightbox';

interface Post {
  id: string;
  userId: string;
  userName: string;
  userPhotoURL: string;
  content: string;
  imageUrl?: string;
  videoUrl?: string;
  likes: number;
  comments: number;
  createdAt: any;
}

interface PostCardProps {
  post: Post;
  isReposted?: boolean;
  isEmbedded?: boolean; // Indica si el post está embebido dentro de otro componente
  isCompact?: boolean; // Indica si se debe mostrar una versión compacta del post
}

const PostCard: React.FC<PostCardProps> = ({ post, isReposted, isEmbedded: _isEmbedded = false, isCompact: _isCompact = false }) => {
  const { currentUser } = useContext(AuthContext);
  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes || 0);
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostText, setRepostText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const { t, i18n } = useTranslation();

  // Generar IDs únicos para accesibilidad
  const postId = useRef(`post-${generateId(post.id)}`);
  const commentsId = useRef(`comments-${generateId(post.id)}`);
  const mediaId = useRef(`media-${generateId(post.id)}`);

  // Check if the current user has liked or bookmarked this post
  useEffect(() => {
    const checkUserInteractions = async () => {
      if (!currentUser) return;

      try {
        // Check if user liked the post
        const likeDocRef = doc(db, `posts/${post.id}/likes`, currentUser.uid);
        const likeDoc = await getDoc(likeDocRef);
        setLiked(likeDoc.exists());

        // Check if user bookmarked the post
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists() && userDoc.data().bookmarks) {
          setBookmarked(userDoc.data().bookmarks.includes(post.id));
        }
      } catch (error) {
        console.error('Error checking user interactions:', error);
      }
    };

    checkUserInteractions();
  }, [currentUser, post.id]);

  const handleLike = async () => {
    if (!currentUser) return;

    try {
      const postRef = doc(db, 'posts', post.id);
      const likeDocRef = doc(db, `posts/${post.id}/likes`, currentUser.uid);

      if (!liked) {
        // Add like
        await Promise.all([
          updateDoc(postRef, {
            likes: increment(1)
          }),
          setDoc(likeDocRef, {
            userId: currentUser.uid,
            userName: currentUser.displayName,
            createdAt: serverTimestamp()
          })
        ]);

        // Notify post author if it's not the current user
        if (post.userId !== currentUser.uid) {
          const notificationRef = doc(collection(db, 'notifications'));
          await setDoc(notificationRef, {
            type: 'like',
            fromUserId: currentUser.uid,
            fromUserName: currentUser.displayName,
            fromUserPhoto: currentUser.photoURL || '',
            toUserId: post.userId,
            postId: post.id,
            read: false,
            createdAt: serverTimestamp()
          });
        }

        setLiked(true);
        setLikesCount(prev => prev + 1);
      } else {
        // Remove like
        await Promise.all([
          updateDoc(postRef, {
            likes: increment(-1)
          }),
          deleteDoc(likeDocRef)
        ]);

        setLiked(false);
        setLikesCount(prev => prev - 1);
      }
    } catch (error) {
      console.error('Error updating like:', error);
    }
  };

  const handleBookmark = async () => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);

      if (!bookmarked) {
        // Add bookmark
        await updateDoc(userRef, {
          bookmarks: arrayUnion(post.id)
        });
        setBookmarked(true);
      } else {
        // Remove bookmark
        await updateDoc(userRef, {
          bookmarks: arrayRemove(post.id)
        });
        setBookmarked(false);
      }
    } catch (error) {
      console.error('Error updating bookmark:', error);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Publicación de ${post.userName} en DomiRed`,
        text: post.content.substring(0, 100) + (post.content.length > 100 ? '...' : ''),
        url: `https://domired.top/post/${post.id}`
      }).catch(error => {
        console.error('Error sharing:', error);
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      const url = `https://domired.top/post/${post.id}`;
      navigator.clipboard.writeText(url).then(() => {
        alert('Enlace copiado al portapapeles!');
      });
    }
  };

  const handleRepost = async () => {
    if (!currentUser) return;

    try {
      // Crear un nuevo documento de repost
      const repostRef = doc(collection(db, 'posts'));
      await setDoc(repostRef, {
        type: 'repost',
        originalPostId: post.id,
        content: repostText.trim(), // Comentario opcional del usuario
        userId: currentUser.uid,
        userName: currentUser.displayName,
        userPhotoURL: currentUser.photoURL || '',
        likes: 0,
        comments: 0,
        createdAt: serverTimestamp()
      });

      // Notificar al autor original si no es el usuario actual
      if (post.userId !== currentUser.uid) {
        const notificationRef = doc(collection(db, 'notifications'));
        await setDoc(notificationRef, {
          type: 'repost',
          fromUserId: currentUser.uid,
          fromUserName: currentUser.displayName,
          fromUserPhoto: currentUser.photoURL || '',
          toUserId: post.userId,
          postId: post.id,
          repostId: repostRef.id,
          read: false,
          createdAt: serverTimestamp()
        });
      }

      // Limpiar y cerrar el modal
      setRepostText('');
      setShowRepostModal(false);

      // Anunciar a lectores de pantalla
      announceToScreenReader(t('accessibility.repostCreated'));
    } catch (error) {
      console.error('Error creating repost:', error);
    }
  };

  const handleDeletePost = async () => {
    if (!currentUser || currentUser.uid !== post.userId || isDeleting) return;

    try {
      setIsDeleting(true);

      // Referencia al documento del post
      const postRef = doc(db, 'posts', post.id);

      // Eliminar el post
      await deleteDoc(postRef);

      // Anunciar a lectores de pantalla
      announceToScreenReader(t('accessibility.postDeleted'));

      // Cerrar el modal de confirmación
      setShowDeleteConfirm(false);

      // Aquí podríamos redirigir al usuario o actualizar la lista de posts
      // Pero como el componente probablemente se desmontará, no es necesario hacer nada más
    } catch (error) {
      console.error('Error deleting post:', error);
      alert(t('post.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  };

  const formatPostDate = (timestamp: any) => {
    if (!timestamp) return '';

    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    // Usar el idioma actual de la aplicación
    const locale = i18n.language.startsWith('es') ? es : enUS;
    return formatDistanceToNow(date, { addSuffix: true, locale });
  };

  return (
    <article
      id={postId.current}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 ${isReposted ? 'border-l-4 border-green-500' : ''}`}
    >
      {/* Post header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <Link to={`/profile/${post.userId}`} className="flex items-center space-x-2">
            <img
              src={post.userPhotoURL || '/default-avatar.png'}
              alt={post.userName}
              className="h-10 w-10 rounded-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = '/default-avatar.png';
              }}
            />
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">{post.userName}</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">{formatPostDate(post.createdAt)}</span>
            </div>
          </Link>
        </div>

        {/* Botón de eliminar (solo visible para el autor) */}
        {currentUser && currentUser.uid === post.userId && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="ml-2 p-1 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t('post.delete')}
            title={t('post.delete')}
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Post content */}
      <div className="block mb-3">
        <div
          className="text-gray-800 dark:text-gray-200 whitespace-pre-line break-words"
          aria-label={t('post.content')}
        >
          <FormattedText text={post.content} />
        </div>
      </div>

      {/* Post media (if any) */}
      {post.imageUrl && (
        <div className="mb-3 rounded-lg overflow-hidden">
          <img
            src={post.imageUrl}
            alt={t('post.imageDescription', { user: post.userName })}
            id={mediaId.current}
            className="w-full h-auto object-contain cursor-pointer transition-transform hover:opacity-95"
            onClick={() => setShowImageLightbox(true)}
            aria-label={t('post.viewFullImage')}
            title={t('post.viewFullImage')}
            aria-describedby={`${postId.current}-media-desc`}
          />
          <span id={`${postId.current}-media-desc`} className="sr-only">
            {t('post.imageDescription', { user: post.userName })}
          </span>
        </div>
      )}

      {/* Image Lightbox */}
      {showImageLightbox && post.imageUrl && (
        <ImageLightbox
          imageUrl={post.imageUrl}
          alt={t('post.imageDescription', { user: post.userName })}
          onClose={() => setShowImageLightbox(false)}
        />
      )}

      {post.videoUrl && (
        <div className="mb-3 rounded-lg overflow-hidden">
          <video
            src={post.videoUrl}
            controls
            className="w-full h-auto max-h-96"
            aria-label={t('post.videoContent')}
            id={mediaId.current}
          >
            <track kind="captions" src="" label={t('accessibility.captions')} />
            <p>{t('accessibility.videoNotSupported')}</p>
          </video>
        </div>
      )}

      {/* Post actions */}
      <div className="flex items-center justify-between mt-4 border-t border-gray-100 dark:border-gray-700 pt-3">
        <button
          onClick={handleLike}
          onKeyDown={handleKeyboardActivation(handleLike)}
          aria-label={liked ? t('post.unlike') : t('post.like')}
          aria-pressed={liked}
          className={`flex items-center text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-all duration-300 ease-in-out transform hover:scale-110 active:scale-90 ${liked ? 'text-red-500 dark:text-red-400' : ''
            }`}
        >
          {liked ? (
            <HeartSolidIcon className="h-5 w-5 mr-1 text-red-500 dark:text-red-400 animate-heart-beat" aria-hidden="true" />
          ) : (
            <HeartIcon className="h-5 w-5 mr-1 transition-transform duration-300 ease-in-out hover:scale-110" aria-hidden="true" />
          )}
          <span aria-live="polite" className={`font-medium ${liked ? 'text-red-500 dark:text-red-400' : ''}`}>{likesCount}</span>
        </button>

        <button
          onClick={() => {
            setShowComments(!showComments);
            if (!showComments) {
              // Anunciar a lectores de pantalla que se han mostrado los comentarios
              announceToScreenReader(t('accessibility.commentsShown'));
            }
          }}
          onKeyDown={handleKeyboardActivation(() => setShowComments(!showComments))}
          aria-expanded={showComments}
          aria-controls={commentsId.current}
          className={`flex items-center transition-all duration-300 ease-in-out transform hover:scale-110 active:scale-90 ${showComments
            ? 'text-blue-500 dark:text-blue-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400'}`}
          aria-label={showComments ? t('post.hideComments') : t('post.viewComments')}
        >
          <ChatBubbleLeftIcon className={`h-5 w-5 mr-1 ${showComments ? 'text-blue-500 dark:text-blue-400' : ''}`} aria-hidden="true" />
          <span className={`font-medium ${showComments ? 'text-blue-500 dark:text-blue-400' : ''}`}>{post.comments || 0}</span>
        </button>

        {!isReposted && (
          <button
            onClick={() => setShowRepostModal(true)}
            onKeyDown={handleKeyboardActivation(() => setShowRepostModal(true))}
            aria-label={t('post.repost')}
            className="flex items-center text-gray-500 dark:text-gray-400 hover:text-green-500 dark:hover:text-green-400 transition-all duration-300 ease-in-out transform hover:scale-110 active:scale-90"
          >
            <ArrowPathRoundedSquareIcon className="h-5 w-5 mr-1 transition-transform hover:rotate-90" aria-hidden="true" />
          </button>
        )}

        <button
          onClick={handleShare}
          onKeyDown={handleKeyboardActivation(handleShare)}
          aria-label={t('post.share')}
          className="flex items-center text-gray-500 dark:text-gray-400 hover:text-green-500 dark:hover:text-green-400 transition-all duration-300 ease-in-out transform hover:scale-110 active:scale-90"
        >
          <ShareIcon className="h-5 w-5 mr-1 transition-transform hover:rotate-12" aria-hidden="true" />
        </button>

        <button
          onClick={handleBookmark}
          onKeyDown={handleKeyboardActivation(handleBookmark)}
          aria-label={bookmarked ? t('post.removeBookmark') : t('post.addBookmark')}
          aria-pressed={bookmarked}
          className={`flex items-center text-gray-500 dark:text-gray-400 hover:text-yellow-500 dark:hover:text-yellow-400 transition-all duration-300 ease-in-out transform hover:scale-110 active:scale-90 ${bookmarked ? 'text-yellow-500 dark:text-yellow-400' : ''
            }`}
        >
          {bookmarked ? (
            <BookmarkSolidIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            <BookmarkIcon className="h-5 w-5" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Comments section (conditionally rendered) */}

      {/* Comments section (conditionally rendered) */}
      {
        showComments && (
          <div
            className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700"
            id={commentsId.current}
            aria-live="polite"
          >
            <h3 className="sr-only">{t('post.comments')}</h3>
            <CommentSection postId={post.id} />
          </div>
        )
      }

      {/* Repost Modal */}
      {
        showRepostModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">{t('post.createRepost')}</h3>
                <button
                  onClick={() => setShowRepostModal(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  aria-label={t('common.close')}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-4">
                <div className="flex items-start space-x-2">
                  <img
                    src={post.userPhotoURL || '/default-avatar.png'}
                    alt=""
                    className="h-8 w-8 rounded-full object-cover"
                    aria-hidden="true"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = '/default-avatar.png';
                    }}
                  />
                  <div>
                    <div className="font-medium">{post.userName}</div>
                    <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line break-words">
                      <FormattedText text={post.content} />
                    </p>
                  </div>
                </div>
              </div>

              <textarea
                value={repostText}
                onChange={(e) => setRepostText(e.target.value)}
                placeholder={t('post.addRepostComment')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700 text-sm"
                rows={3}
              />

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => setShowRepostModal(false)}
                  className="px-4 py-2 mr-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleRepost}
                  className="px-4 py-2 bg-domired-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500"
                >
                  {t('post.repost')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('post.confirmDelete')}</h3>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  aria-label={t('common.close')}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('post.deleteWarning')}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  disabled={isDeleting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleDeletePost}
                  className="px-4 py-2 bg-red-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  disabled={isDeleting}
                >
                  {isDeleting ? t('common.deleting') : t('common.delete')}
                </button>
              </div>
            </div>
          </div>
        )
      }
    </article >
  );
};

export default PostCard;
